import * as _ from 'lodash-es';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Cards } from '../../components/Card';
import { DEFAULT_DATES } from '../../components/DateRangePicker';
import EmptyState from '../../components/EmptyState';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import { gql } from '../../graphql/generated/gql';
import type { AuditLogType } from '../../graphql/generated/graphql';
import type { EntitySearchResult } from '../../search/entitySearch/EntitySelect';
import SettingsContainerView from '../../settings/SettingsContainerView';
import ConnectionHelper from '../../utils/graphql/ConnectionHelper';
import AuditFilter from './AuditFilter';
import * as styles from './AuditLogContentView.module.scss';
import AuditLogRow from './AuditLogRow';

const AUDIT_VIEW_QUERY = gql(/** @complexity 24 */ `
  query AuditLogContentView(
    $afterCursor: String
    $types: [AuditLogType!]
    $user: ID
    $startDate: Date!
    $endDate: Date!
  ) {
    auditLogs(
      first: 100
      after: $afterCursor
      types: $types
      user: $user
      startDate: $startDate
      endDate: $endDate
    ) {
      edges {
        node {
          ...AuditLogRow_auditLog
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const PROVISIONS_QUERY = gql(/** @complexity 4 */ `
  query AuditLogProvisions {
    currentSession {
      organization {
        id
        provisions {
          ...AuditLogRow_provisions
        }
      }
    }
  }
`);

type Dates = {
  startAt: moment.Moment | null;
  endAt: moment.Moment | null;
};

const AuditLogContentView = () => {
  const [action, setAction] = useState<AuditLogType | null>(null);
  const [entity, setEntity] = useState<EntitySearchResult | null>(null);
  const [dates, setDates] = useState<Dates>({
    startAt: moment(DEFAULT_DATES.startAt),
    endAt: moment(DEFAULT_DATES.endAt),
  });

  const response = useQuery(AUDIT_VIEW_QUERY, {
    variables: {
      types: action ? [action] : undefined,
      user: entity?.id,
      startDate: dates.startAt?.format('YYYY-MM-DD'),
      endDate: dates.endAt?.format('YYYY-MM-DD'),
    },
  });

  const { data, loading } = response;

  const fetchMore = ConnectionHelper.generateFetchMore(
    AUDIT_VIEW_QUERY,
    response,
    'auditLogs',
  );

  const auditLogs = _.compact(
    data?.auditLogs?.edges?.map((edge) => edge?.node),
  );

  const { data: provisionsData } = useQuery(PROVISIONS_QUERY);
  const provisions =
    provisionsData?.currentSession.organization.provisions ?? [];

  const filterProps = useMemo(
    () => ({
      action,
      setAction,
      entity,
      setEntity,
      onDatesChange: setDates,
    }),
    [action, entity, setAction, setEntity, setDates],
  );

  return (
    <SettingsContainerView title="Audit Logs">
      <AuditFilter {...filterProps} />

      {loading && (
        <div className={styles.loadingContainer}>
          <LoadingIndicator />
        </div>
      )}
      {!loading && auditLogs.length > 0 && (
        <>
          <Cards>
            {auditLogs.map((auditLog) => (
              <AuditLogRow
                key={auditLog.id}
                auditLog={auditLog}
                provisions={provisions}
              />
            ))}
          </Cards>
          <InfiniteScrollWaypoint fetchMore={fetchMore} />
        </>
      )}
      {!loading && auditLogs.length === 0 && (
        <div className={styles.emptyStateContainer}>
          <EmptyState
            imageName="notFound"
            title="No results found"
            message="Try applying different filters to see more results."
          />
        </div>
      )}
    </SettingsContainerView>
  );
};

export default AuditLogContentView;
